import { useLocation, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { canShowNavItem, isNavItemDisabled } from './navVisibility';
import type { NavConfig, NavItem, NavBadgeCounts } from '@/config/nav.types';
import type { ProducerCapabilities as RoleCapabilities } from '@/hooks/useProducerAccess';
import type { ProducerMeta, ProducerCapabilityKey } from '@/lib/capabilities';
import type { ProducerFeatureNavItem } from '@/config/producerFeatures';
import type { EffectiveFeatureMap } from '@/hooks/useProducerEffectiveFeatures';
import { Sprout, ArrowLeft, ChevronRight, Lock, Ban, ArrowUpRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { can } from '@/lib/capabilities';

interface AppSidebarProps {
  navConfig: NavConfig;
  contextTitle?: string;
  backHref?: string;
  backLabel?: string;
  enabledCapabilities?: string[];
  /** If true, show all capability items with On/Off badges (for admin) */
  showAllWithStatusBadges?: boolean;
  /** Dynamic badge counts by key */
  badgeCounts?: NavBadgeCounts;
  /** User's role capabilities for filtering nav items */
  roleCapabilities?: RoleCapabilities;
  /** If true, user is viewer (read-only mode) */
  isViewerRole?: boolean;
  /** Producer lifecycle meta from capabilities view */
  producerMeta?: ProducerMeta | null;
  /** Resolved capabilities map from producer_capabilities_v */
  capabilitiesMap?: Record<ProducerCapabilityKey, boolean> | null;
  /** Feature nav items driven by effective plan + overrides */
  featureNavItems?: ProducerFeatureNavItem[];
  /** Effective feature map from producer_effective_features_v */
  effectiveFeatures?: EffectiveFeatureMap;
  /** Base href prefix for feature items (e.g. /app/producer/farm/{farmId}) */
  featureBaseHref?: string;
}

export function AppSidebar({
  navConfig,
  contextTitle,
  backHref,
  backLabel,
  enabledCapabilities = [],
  showAllWithStatusBadges = false,
  badgeCounts = {},
  roleCapabilities,
  isViewerRole = false,
  producerMeta,
  capabilitiesMap,
  featureNavItems = [],
  effectiveFeatures = {},
  featureBaseHref = '',
}: AppSidebarProps) {
  const location = useLocation();
  const { state: sidebarState } = useSidebar();
  const isCollapsed = sidebarState === 'collapsed';

  const isVerified = producerMeta?.isVerified ?? false;
  const isSuspended = producerMeta?.isSuspended ?? false;

  // Shared visibility context – same rules as AppMobileBottomNav
  const visCtx = {
    enabledCapabilities,
    roleCapabilities,
    isViewerRole,
    producerMeta,
    capabilitiesMap,
    showAllWithStatusBadges,
  };

  const isActive = (href: string, exact?: boolean) => {
    if (exact) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  const shouldShowItem = (item: NavItem) => canShowNavItem(item, visCtx);

  const isItemDisabled = (item: NavItem) => isNavItemDisabled(item, visCtx).disabled;

  const isLockedForVerificationFn = (item: NavItem) => {
    return item.requiresVerification && !isVerified;
  };

  const getViewerBadge = (item: NavItem) => {
    if (!isViewerRole || !item.viewerReadOnly) return null;
    return (
      <Badge variant="outline" className="ml-auto text-[10px] px-1.5 text-muted-foreground">
        Endast läsning
      </Badge>
    );
  };

  const getItemStatusBadge = (item: NavItem) => {
    if (!showAllWithStatusBadges || !item.requireCapability) return null;
    const isEnabled = !item.requireCapability || enabledCapabilities.includes(item.requireCapability);
    return (
      <Badge
        variant={isEnabled ? 'default' : 'outline'}
        className={cn(
          'ml-auto text-[10px] px-1.5',
          isEnabled ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
        )}
      >
        {isEnabled ? 'På' : 'Av'}
      </Badge>
    );
  };

  const getItemBadgeCount = (badgeKey?: string) => {
    if (!badgeKey) return null;
    const count = badgeCounts[badgeKey];
    if (!count || count <= 0) return null;
    return (
      <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1.5 text-xs">
        {count > 99 ? '99+' : count}
      </Badge>
    );
  };

  const getVerificationLockIcon = (item: NavItem) => {
    if (isSuspended) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Ban className="h-3.5 w-3.5 ml-auto text-destructive shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="right"><p>Gården är avstängd</p></TooltipContent>
        </Tooltip>
      );
    }
    if (!isLockedForVerificationFn(item)) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Lock className="h-3.5 w-3.5 ml-auto text-muted-foreground shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="right"><p>Kräver verifierad gård</p></TooltipContent>
      </Tooltip>
    );
  };

  // Feature nav items are no longer rendered as a separate section.
  // Sub-pages are defined as children in nav.producer.ts and rendered
  // by renderMenuItem via Collapsible sub-menus.


  const renderMenuItem = (item: NavItem) => {
    const disabled = isItemDisabled(item);
    const active = isActive(item.href, item.exact);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = hasChildren && item.children!.some((child) => location.pathname.startsWith(child.href));
    const isVerificationLocked = isLockedForVerificationFn(item);

    if (hasChildren && !disabled && !isVerificationLocked) {
      return (
        <Collapsible key={item.href} defaultOpen={isExpanded || active} className="group/collapsible">
          <SidebarMenuItem>
            <div className="flex items-center">
              <SidebarMenuButton
                asChild
                isActive={active || isExpanded}
                tooltip={item.label}
                className="flex-1 min-w-0"
              >
                <NavLink to={item.href} end className="flex items-center gap-2" activeClassName="">
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!isCollapsed && (
                    <>
                      <span className="truncate">{item.label}</span>
                      {getItemStatusBadge(item)}
                    </>
                  )}
                </NavLink>
              </SidebarMenuButton>
              {!isCollapsed && (
                <CollapsibleTrigger asChild>
                  <button className="p-1 rounded-md hover:bg-muted/50 transition-colors shrink-0 mr-1">
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </button>
                </CollapsibleTrigger>
              )}
            </div>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.children!.map((child) => (
                  <SidebarMenuSubItem key={child.href}>
                    <SidebarMenuSubButton asChild isActive={isActive(child.href)}>
                      <NavLink to={child.href} end={child.exact} className="flex items-center justify-between gap-2 w-full" activeClassName="">
                        <span>{child.label}</span>
                        {getItemBadgeCount(child.badgeKey)}
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem key={item.href}>
        <SidebarMenuButton
          asChild={!disabled && !isVerificationLocked}
          isActive={active}
          tooltip={item.label}
          className={cn((disabled || isVerificationLocked) && 'opacity-50 cursor-not-allowed')}
        >
          {disabled || isVerificationLocked ? (
            <div className="flex items-center gap-2 w-full">
              <item.icon className="h-4 w-4 shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="truncate">{item.label}</span>
                  {isVerificationLocked ? (
                    getVerificationLockIcon(item)
                  ) : (
                    <Badge variant="secondary" className="ml-auto text-[10px] px-1.5">Snart</Badge>
                  )}
                </>
              )}
            </div>
          ) : (
            <NavLink to={item.href} end={item.exact} className="flex items-center gap-2 w-full" activeClassName="">
              <item.icon className="h-4 w-4 shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="truncate">{item.label}</span>
                  {getItemStatusBadge(item)}
                  {getViewerBadge(item)}
                </>
              )}
            </NavLink>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sprout className="h-4 w-4" />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold truncate">{contextTitle || 'Farmly'}</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {navConfig.map((section, sectionIndex) => (
          <SidebarGroup key={sectionIndex}>
            {section.title && <SidebarGroupLabel>{section.title}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.filter(shouldShowItem).map(renderMenuItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        {backHref && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={backLabel || 'Tillbaka'}>
                <Link to={backHref} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  {!isCollapsed && <span>{backLabel || 'Tillbaka'}</span>}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
